import { PROGRAM_LIMITS, RAISER_LABELS } from './constants';
import { t } from '../i18n';
import type { ProgramLimits } from './constants';
import type { FigureVariant, Programme } from '../types';

/** A programme's figures, resolved against the catalogue in the same order
 *  as `Programme.program` — see `resolveProgram()`/`resolveProgramForType()`
 *  in `src/data/catalogue.ts`. `undefined` for a figure number the
 *  catalogue doesn't carry (still loading, or a stale id). */
export type ResolvedProgram = ReadonlyArray<FigureVariant | undefined>;

export interface RuleIssue {
  level: 'error' | 'warning';
  message: string;
  /** Index into `Programme.program` the issue is anchored to, if any. */
  index?: number;
}

/** UCI 8.2.002/8.2.003 limits for this programme, or null when no rule is
 *  recorded for its type (Team 6 — see CLAUDE.md). */
export function programLimits(p: Programme): ProgramLimits | null {
  return PROGRAM_LIMITS[p.ageTier][p.type] ?? null;
}

function tierArticle(p: Programme): string {
  return p.ageTier === 'youth' ? '8.2.003' : '8.2.002';
}

/**
 * The "figure" a catalogue variant belongs to, e.g. "2011a"/"2011b"/"2011c"
 * all belong to figure "2011" — competition rules forbid using more than one
 * variant of the same figure in a programme. Only real catalogue ids (digits
 * followed by one or two letters, see CLAUDE.md's figure-numbering note) are
 * grouped this way; anything else (synthetic ids in tests, imported data
 * that doesn't match the pattern) is left as its own, ungrouped base so it
 * can't collide by accident.
 */
function figureBase(nr: string): string {
  const m = /^(\d+)[a-zA-Z]+$/.exec(nr);
  return m ? m[1]! : nr;
}

/**
 * UCI 8.2.002/8.2.003 §2 "Number of figures": the total figure count per
 * starter type and age tier (`PROGRAM_LIMITS`, `constants.ts`).
 */
function checkFigureCountLimit(p: Programme, limits: ProgramLimits, article: string): RuleIssue[] {
  if (p.program.length <= limits.maxTotal) return [];
  return [
    {
      level: 'error',
      message: t('rules.count.exceeded', { count: p.program.length, max: limits.maxTotal, article }),
    },
  ];
}

/**
 * UCI 8.2.002/8.2.003 §2 "Number of figures", pair-specific clause: "It is
 * required to perform figures on one and on two bicycles", with the
 * one-bicycle count bounded (`onBikeMin`/`onBikeMax`, `PROGRAM_LIMITS`).
 * Two-bicycle figures have no separate band — they're just whatever the
 * total leaves once the one-bicycle count is satisfied.
 */
function checkOneBicycleFigureBand(p: Programme, resolved: ResolvedProgram, limits: ProgramLimits, article: string): RuleIssue[] {
  if (p.type !== 'pair' || p.program.length === 0 || limits.onBikeMin === undefined) return [];
  const issues: RuleIssue[] = [];
  const onOne = resolved.filter((v) => v?.bikes === 1).length;
  const onTwo = resolved.filter((v) => v?.bikes === 2).length;

  if (onOne < limits.onBikeMin) {
    issues.push({ level: 'error', message: t('rules.onBike.tooFew', { count: onOne, min: limits.onBikeMin, article }) });
  } else if (limits.onBikeMax !== undefined && onOne > limits.onBikeMax) {
    issues.push({ level: 'error', message: t('rules.onBike.tooMany', { count: onOne, max: limits.onBikeMax, article }) });
  }
  if (onTwo === 0) {
    issues.push({ level: 'error', message: t('rules.onBike.needTwo', { article }) });
  }
  return issues;
}

/**
 * UCI 8.2.006 "Leaving bicycles": riders may not leave the bicycle during
 * the riding performance, except — pair artistic cycling only — "the
 * one-time change from two bicycles to one, or from one bicycle to two".
 * Once the programme has moved from one bicycle count to the other, it
 * can't move back. Figures without a known `bikes` (Team 4/6 catalogues
 * never set it, see CLAUDE.md) don't participate in the sequence.
 */
function checkBicycleChangeOnce(p: Programme, resolved: ResolvedProgram): RuleIssue[] {
  if (p.type !== 'pair') return [];
  const issues: RuleIssue[] = [];
  let switches = 0;
  let prevBikes: 1 | 2 | undefined;
  p.program.forEach((u, i) => {
    const bikes = resolved[i]?.bikes;
    if (bikes === undefined) return;
    if (prevBikes !== undefined && bikes !== prevBikes) {
      switches++;
      if (switches > 1) {
        issues.push({
          level: 'error',
          index: i,
          message: t('rules.bikes.multipleSwitches', { nr: u.nr, pos: i + 1 }),
        });
      }
    }
    prevBikes = bikes;
  });
  return issues;
}

/**
 * UCI 8.1.019 "Completion of the evaluation sheet and compilation of riding
 * performance": "Only one figure of each group of figures (a, b, c etc.)
 * can be listed on the evaluation sheet." A *different* variant of a figure
 * already used (see figureBase()) can't be added. The same variant entered
 * twice is deliberately left to duplicateNumbers() (workspace.ts) instead,
 * which warns without blocking export — a coach re-adding the identical
 * exercise is a likely slip, not the kind of rule violation this error is
 * for.
 */
function checkFigureNotRepeated(p: Programme): RuleIssue[] {
  const issues: RuleIssue[] = [];
  const firstSeenAt = new Map<string, number>();
  p.program.forEach((u, i) => {
    const base = figureBase(u.nr);
    const first = firstSeenAt.get(base);
    if (first === undefined) {
      firstSeenAt.set(base, i);
      return;
    }
    const firstExercise = p.program[first]!;
    if (firstExercise.nr === u.nr) return;
    issues.push({
      level: 'error',
      index: i,
      message: t('rules.figure.repeated', { nr: u.nr, pos: i + 1, first: firstExercise.nr, firstPos: first + 1 }),
    });
  });
  return issues;
}

/**
 * UCI 8.2.011 "Final figures": a final figure "can only be performed as the
 * last figure before the change of bicycles in pair artistic cycling or as
 * the last figure of the riding performance".
 */
function checkFinalFigurePlacement(p: Programme, resolved: ResolvedProgram): RuleIssue[] {
  const issues: RuleIssue[] = [];
  const program = p.program;
  program.forEach((u, i) => {
    const cur = resolved[i];
    if (!cur?.final || i === program.length - 1) return;
    const nextBikes = resolved[i + 1]?.bikes;
    if (p.type === 'pair' && cur.bikes !== undefined && nextBikes !== undefined && nextBikes !== cur.bikes) {
      return;
    }
    issues.push({
      level: 'error',
      index: i,
      message: t(p.type === 'pair' ? 'rules.final.pair' : 'rules.final.single', { nr: u.nr, pos: i + 1 }),
    });
  });
  return issues;
}

/**
 * UCI 8.2.028 "Passages (P.)": "If the described endposition is a
 * raiser-position, the following figure must be shown in the same kind of
 * raiser." Under-reports rather than guesses: compound pair-on-one-bicycle
 * passages (`"X / Y"` names) never get raiser metadata from the build
 * script, so they're silently skipped here too — see CLAUDE.md.
 */
function checkRaiserContinuity(p: Programme, resolved: ResolvedProgram): RuleIssue[] {
  const issues: RuleIssue[] = [];
  const program = p.program;
  for (let i = 0; i < program.length - 1; i++) {
    const cur = resolved[i];
    if (!cur?.raiserEnd) continue;
    const next = resolved[i + 1];
    const nextKind = next?.raiser ?? next?.raiserStart;
    if (nextKind !== cur.raiserEnd) {
      const label = RAISER_LABELS[cur.raiserEnd] ?? cur.raiserEnd;
      issues.push({
        level: 'warning',
        index: i,
        message: t('rules.raiser.continuity', { nr: program[i]!.nr, pos: i + 1, label }),
      });
    }
  }
  return issues;
}

/**
 * Checks a programme against the rules in UCI Chapter II §2/§8 (8.1.019
 * figure repetition, 8.2.002/8.2.003 figure counts, 8.2.006 bicycle change,
 * 8.2.011 final figures, 8.2.028 raiser continuity) that the XSDs cannot
 * express. `type`/`ageTier` live directly on `Programme`; `resolved` is
 * `p.program` resolved against the catalogue (`resolveProgramForType()`,
 * `src/data/catalogue.ts`) — this itself stays pure and framework-agnostic,
 * it just needs the resolution done by the caller, since fetching a
 * catalogue is inherently async. Runs both in `xml/validate.ts`
 * (export-time) and directly in the programme editor, as the coach builds
 * it.
 */
export function checkProgram(p: Programme, resolved: ResolvedProgram): RuleIssue[] {
  const limits = programLimits(p);
  const article = tierArticle(p);

  return [
    ...(limits ? checkFigureCountLimit(p, limits, article) : []),
    ...(limits ? checkOneBicycleFigureBand(p, resolved, limits, article) : []),
    ...checkBicycleChangeOnce(p, resolved),
    ...checkFigureNotRepeated(p),
    ...checkFinalFigurePlacement(p, resolved),
    ...checkRaiserContinuity(p, resolved),
  ];
}
